import { useContext, useState } from 'react'
import axios from 'axios'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import { Header, OtpInput, Button, LoadingSpinner, Icon } from '../../Generic'

import '../GoToMobile.css'
import { forceRedirect } from '@methods/forceRedirect'
import { devLog } from '@methods/devLog'
import { useKeyboardAwareViewport } from '@/hooks/useKeyboardAwareViewport'
import '@/components/Generic/OtpInput/OtpInput.css'
import { cx } from '@/utils/cx'

interface EnterPinProps {
  nextStep: () => void
  maxAttempts: number
  verifyPinCodeUrl?: string
  closeMobileSessionUrl?: string
}

export const EnterPin = observer(
  ({ nextStep, maxAttempts, verifyPinCodeUrl, closeMobileSessionUrl }: EnterPinProps) => {
    const viewport = useKeyboardAwareViewport()

    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { switchToMobile: trans } = store.TranslationsState.translations

    const [isCodeBeingChecked, setIsCodeBeingChecked] = useState(false)
    const [isBeingRedirected, setIsBeingRedirected] = useState(false)
    const [isCodeIncorrect, setIsCodeIncorrect] = useState(false)
    const [attemptsLeft, setAttemptsLeft] = useState(maxAttempts)

    const [pin, setPin] = useState('')

    const handlePinSubmit = async () => {
      if (!verifyPinCodeUrl || !closeMobileSessionUrl) return
      setIsCodeBeingChecked(true)
      try {
        const { data } = await axios.post(
          verifyPinCodeUrl,
          { pinCode: pin },
          { withCredentials: true }
        )

        if (data?.shouldCloseSession) {
          setIsBeingRedirected(true)
          forceRedirect(closeMobileSessionUrl)
        } else if (data?.isPinCodeCorrect) {
          setIsBeingRedirected(true)
          forceRedirect(data.completeProceedUrl)
        } else if (attemptsLeft > 0) {
          setIsCodeIncorrect(true)
          setPin('')
          setAttemptsLeft(attemptsLeft - 1)
        } else {
          setIsBeingRedirected(true)
          nextStep()
        }
      } catch (error) {
        devLog(error)
      }
      setIsCodeBeingChecked(false)
    }

    return (
      <>
        <Header className="m-30-0">{trans.enterPin.header}</Header>

        <p className="generic__intro-paragraph centered">{trans.enterPin.description}</p>

        <div className="layout__margined m-0-0-30" />

        <div
          className={cx(
            'go-to-mobile__code-input-wrapper',
            isCodeBeingChecked && 'go-to-mobile__code-input-wrapper--loading',
            isCodeIncorrect && 'go-to-mobile__code-input-wrapper--error'
          )}
        >
          <OtpInput
            value={pin}
            valueLength={4}
            onChange={(val) => {
              setPin(val)
              setIsCodeIncorrect(false)
            }}
            className="otp-input__container--narrow otp-input__container--compact"
          />

          {isCodeIncorrect && (
            <>
              <div className="generic__spacer h-15" />
              <div
                className={cx(
                  'generic__paragraph-centered',
                  'flex-row-center',
                  'generic__error-message'
                )}
              >
                <Icon type="info" size="18px" color="#D83535" />
                {trans.enterPin.error1}
              </div>
            </>
          )}

          {attemptsLeft < maxAttempts && attemptsLeft > 1 && (
            <>
              <div className="generic__spacer h-15" />
              <div className={cx('generic__paragraph-centered', 'flex-row-center')}>
                <Icon type="info" size="18px" color="#3131D9" />
                {trans.enterPin?.attemptsLeft.replace('<number>', attemptsLeft.toString())}
              </div>
            </>
          )}

          {attemptsLeft === 1 && (
            <>
              <div className="generic__spacer h-15" />
              <div
                className={cx(
                  'generic__paragraph-centered',
                  'flex-row-center',
                  'generic__warning-message'
                )}
              >
                <Icon type="warning" size="18px" color="#FFA500" />
                {trans.enterPin.lastAttempt}
              </div>
            </>
          )}
        </div>

        {!isCodeBeingChecked && !isBeingRedirected ? (
          <div
            className="layout__content-container-bottom-fixed"
            style={
              viewport?.keyboardHeight
                ? { top: 'unset', bottom: `${viewport.keyboardHeight}px` }
                : undefined
            }
          >
            {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
            <Button disabled={isCodeBeingChecked || pin.length < 4} onClick={handlePinSubmit}>
              <span className="generic__button-text">{trans.enterPin.continue}</span>
            </Button>
          </div>
        ) : (
          <LoadingSpinner className="go-to-mobile__loading-spinner" />
        )}

        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </>
    )
  }
)
